import { useState, useEffect } from 'react';
import { 
  getAllUsers, 
  createUser, 
  deleteUser, 
  updateUserRole, 
  User, 
  RegisterData 
} from '../api/client';

interface UserManagementProps {
  currentUser: User;
}

export default function UserManagement({ currentUser }: UserManagementProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Create user form
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState<'admin' | 'user'>('user');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllUsers();
      setUsers(response.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);

    try {
      const data: RegisterData = {
        email: newUserEmail,
        password: newUserPassword,
        role: newUserRole,
      };

      await createUser(data);
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserRole('user');
      setShowCreateForm(false);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      setError(null);
      await deleteUser(userId);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const handleToggleRole = async (user: User) => {
    const newRole = user.role === 'admin' ? 'user' : 'admin';
    
    if (!confirm(`Change ${user.email}'s role to ${newRole}?`)) {
      return;
    }

    try {
      setError(null);
      await updateUserRole(user.id, newRole);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user role');
    }
  };

  if (currentUser.role !== 'admin') {
    return null;
  }

  return (
    <div className="container user-management">
      <h2>👥 User Management</h2>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="user-management-header">
        <button
          className="create-user-btn"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : '➕ Create New User'}
        </button>
      </div>

      {showCreateForm && (
        <div className="create-user-form">
          <h3>Create New User</h3>
          <form onSubmit={handleCreateUser}>
            <div className="form-group">
              <label htmlFor="newEmail">Email</label>
              <input
                type="email"
                id="newEmail"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                required
                disabled={creating}
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">Password</label>
              <input
                type="password"
                id="newPassword"
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
                required
                minLength={6}
                disabled={creating}
              />
            </div>

            <div className="form-group">
              <label htmlFor="newRole">Role</label>
              <select
                id="newRole"
                value={newUserRole}
                onChange={(e) => setNewUserRole(e.target.value as 'admin' | 'user')}
                disabled={creating}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <button type="submit" className="submit-btn" disabled={creating}>
              {creating ? 'Creating...' : 'Create User'}
            </button>
          </form>
        </div>
      )}

      {loading ? (
        <p>Loading users...</p>
      ) : (
        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge role-${user.role}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString()
                      : 'N/A'}
                  </td>
                  <td className="actions">
                    {user.id !== currentUser.id && (
                      <>
                        <button
                          onClick={() => handleToggleRole(user)}
                          className="action-btn role-btn"
                          title={`Change to ${user.role === 'admin' ? 'user' : 'admin'}`}
                        >
                          {user.role === 'admin' ? '👤' : '👑'}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="action-btn delete-btn"
                          title="Delete user"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                    {user.id === currentUser.id && (
                      <span className="current-user-badge">You</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {users.length === 0 && (
            <p className="no-users">No users found</p>
          )}
        </div>
      )}
    </div>
  );
}

